import {CSSProperties, MouseEvent, useEffect, useMemo, useState} from 'react'
import {Button} from 'react-bootstrap'
import {toast} from 'react-toastify'

import {formatPhoneNumber} from '../../helpers'
import {useTelephony} from '../../hooks/useTelephony'
import {formatPhoneDisplay} from '../../../../utils/phoneUtils'

const TELEPHONY_CUSTOM_PHONE_ACTIONS_CLASS = 'telephony-custom-phone-actions'
const TELEPHONY_CUSTOM_PHONE_ACTIONS_COUNT = 'telephonyCustomPhoneActionsCount'

type SupportedProvider = 'RINGCENTRAL' | 'VICIDIAL'

interface PhoneProviderActionsProps {
  phone?: string | null
  leadId?: number | string | null
  displayText?: string | null
  emptyText?: string
  className?: string
  textClassName?: string
  textStyle?: CSSProperties
  stopPropagation?: boolean
  alwaysShowSelector?: boolean
}

export const PhoneProviderActions: React.FC<PhoneProviderActionsProps> = ({
  phone,
  leadId,
  displayText,
  emptyText = 'N/R',
  className,
  textClassName,
  textStyle,
  stopPropagation = true,
  alwaysShowSelector = false,
}) => {
  const telephony = useTelephony()
  const [isHovered, setIsHovered] = useState(false)
  const [selectedProvider, setSelectedProvider] = useState<SupportedProvider | null>(null)

  const canCallWithRingCentral = telephony.isRingCentralConnected
  const canCallWithVicidial =
    telephony.config.vicidial_enabled &&
    Boolean(telephony.config.vicidial_agent_user) &&
    (telephony.vicidialSession.activeSession || telephony.vicidialSession.connected)

  const hasProviders = canCallWithRingCentral || canCallWithVicidial
  const normalizedPhone = phone?.trim() || ''
  const phoneLabel = useMemo(() => {
    if (displayText && displayText.trim().length > 0) return displayText
    if (!normalizedPhone) return emptyText
    return formatPhoneDisplay(normalizedPhone)
  }, [displayText, emptyText, normalizedPhone])

  useEffect(() => {
    if (!hasProviders || typeof document === 'undefined') return

    const body = document.body
    const currentCount = Number(body.dataset[TELEPHONY_CUSTOM_PHONE_ACTIONS_COUNT] || '0')
    body.dataset[TELEPHONY_CUSTOM_PHONE_ACTIONS_COUNT] = String(currentCount + 1)
    body.classList.add(TELEPHONY_CUSTOM_PHONE_ACTIONS_CLASS)

    return () => {
      const nextCount = Number(body.dataset[TELEPHONY_CUSTOM_PHONE_ACTIONS_COUNT] || '1') - 1
      if (nextCount <= 0) {
        delete body.dataset[TELEPHONY_CUSTOM_PHONE_ACTIONS_COUNT]
        body.classList.remove(TELEPHONY_CUSTOM_PHONE_ACTIONS_CLASS)
        return
      }

      body.dataset[TELEPHONY_CUSTOM_PHONE_ACTIONS_COUNT] = String(nextCount)
    }
  }, [hasProviders])

  const stopEvent = (event: MouseEvent<HTMLElement>) => {
    if (stopPropagation) event.stopPropagation()
  }

  const stopActionEvent = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
  }

  const handleRingCentralCall = async (event: MouseEvent<HTMLButtonElement>) => {
    stopActionEvent(event)
    if (!normalizedPhone) {
      toast.error('This record has no phone number.')
      return
    }

    await telephony.startCall({phone: normalizedPhone, leadId, providerOverride: 'RINGCENTRAL'})
  }

  const handleRingCentralSms = (event: MouseEvent<HTMLButtonElement>) => {
    stopActionEvent(event)
    if (!normalizedPhone) {
      toast.error('This record has no phone number.')
      return
    }

    if (!telephony.isRingCentralConnected) {
      toast.error('RingCentral is not connected in this browser.')
      return
    }

    if (!(window as any).RCAdapter?.clickToSMS) {
      toast.error('RingCentral is still loading. Try again in a moment.')
      return
    }

    ;(window as any).RCAdapter.clickToSMS(formatPhoneNumber(normalizedPhone))
    toast.success('SMS composer opened in RingCentral.')
  }

  const handleVicidialCall = async (event: MouseEvent<HTMLButtonElement>) => {
    stopActionEvent(event)
    if (!normalizedPhone) {
      toast.error('This record has no phone number.')
      return
    }

    if (!telephony.vicidialSession.connected) {
      toast.error('Connect PolicyDial from Integrations before starting a call.')
      return
    }

    telephony.prepareVicidialDial(normalizedPhone, leadId, true)
  }

  return (
    <div
      className={className || 'd-inline-flex flex-column align-items-start gap-1'}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false)
        if (!alwaysShowSelector) {
          setSelectedProvider(null)
        }
      }}
      onClick={(event) => stopEvent(event)}
    >
      <div className='d-flex align-items-center gap-2'>
        <span className={textClassName} style={textStyle}>
          {phoneLabel}
        </span>
        {normalizedPhone && hasProviders && (isHovered || alwaysShowSelector) && (
          <div className='d-flex align-items-center gap-1'>
            {canCallWithRingCentral && (
              <Button
                variant='light'
                size='sm'
                className='border d-flex align-items-center justify-content-center px-2 py-1'
                style={{
                  backgroundColor: '#fff',
                  borderColor:
                    selectedProvider === 'RINGCENTRAL' ? '#0d6efd' : '#d7dbe7',
                  boxShadow:
                    selectedProvider === 'RINGCENTRAL'
                      ? '0 0 0 2px rgba(13,110,253,0.12)'
                      : 'none',
                }}
                title='RingCentral options'
                onClick={(event) => {
                  stopActionEvent(event)
                  setSelectedProvider('RINGCENTRAL')
                }}
              >
                <img
                  src='/media/tfi/ringcentral-mark.svg'
                  alt='RingCentral'
                  style={{width: 22, height: 22, objectFit: 'contain'}}
                />
              </Button>
            )}
            {canCallWithVicidial && (
              <Button
                variant='light'
                size='sm'
                className='border d-flex align-items-center justify-content-center p-1'
                style={{
                  backgroundColor: '#fff',
                  borderColor: selectedProvider === 'VICIDIAL' ? '#0d6efd' : '#d7dbe7',
                  boxShadow:
                    selectedProvider === 'VICIDIAL'
                      ? '0 0 0 2px rgba(13,110,253,0.12)'
                      : 'none',
                }}
                title='PolicyDial options'
                onClick={(event) => {
                  stopActionEvent(event)
                  setSelectedProvider('VICIDIAL')
                }}
              >
                <img
                  src='/media/tfi/policydial-icon.svg'
                  alt='PolicyDial'
                  style={{width: 22, height: 22, objectFit: 'contain'}}
                />
              </Button>
            )}
          </div>
        )}
      </div>

      {normalizedPhone && selectedProvider && (
        <div className='d-flex align-items-center gap-1 rounded border bg-white px-2 py-1 shadow-sm'>
          {selectedProvider === 'RINGCENTRAL' ? (
            <>
              <Button
                variant='light'
                size='sm'
                className='border d-flex align-items-center gap-1 px-2 py-1'
                title='Call with RingCentral'
                onClick={handleRingCentralCall}
              >
                <img
                  src='/media/tfi/ringcentral-mark.svg'
                  alt='RingCentral'
                  style={{width: 16, height: 16, objectFit: 'contain'}}
                />
                <i className='bi bi-telephone-fill text-primary' />
              </Button>
              <Button
                variant='light'
                size='sm'
                className='border d-flex align-items-center gap-1 px-2 py-1'
                title='Text with RingCentral'
                onClick={handleRingCentralSms}
              >
                <img
                  src='/media/tfi/ringcentral-mark.svg'
                  alt='RingCentral'
                  style={{width: 16, height: 16, objectFit: 'contain'}}
                />
                <i className='bi bi-chat-dots-fill text-primary' />
              </Button>
            </>
          ) : (
            <>
              <Button
                variant='light'
                size='sm'
                className='border d-flex align-items-center gap-2 px-2 py-1'
                title='Call with PolicyDial'
                onClick={handleVicidialCall}
              >
                <img
                  src='/media/tfi/policydial-icon.svg'
                  alt='PolicyDial'
                  style={{width: 20, height: 20, objectFit: 'contain'}}
                />
                <i className='bi bi-telephone-fill text-primary' />
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  )
}
